import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../../api/client'
import { useDeviceTypes } from '../../api/queries'
import type {
  Customer,
  CustomerDetail,
  Device,
  VpnLanCheck,
  VpnSession,
  VpnSessionList,
  VpnSite,
  VpnSiteList,
  VpnSiteStatus,
  VpnStatus,
  VpnTechnician
} from '../../api/types'
import { Card, EmptyState, ErrorBox, Loading } from '../../components/ui'
import { formatDateTime, formatTime } from '../../lib/format'
import { aliasExample } from '../../lib/vpnLan'
import {
  buildEdgeOsRouterCommands,
  buildRouterConfig,
  downloadTextFile,
  edgeOsCommandsFileName,
  generateWireGuardKeyPair,
  routerConfigFileName,
  type WireGuardKeyPair
} from '../../lib/wireguard'

/**
 * VPN til kundeanlæg (beslutning 091). Kun Superadmin.
 *
 * Overblik bestemmer, VPN-gatewayen på vpn.tc-el.dk håndhæver: al adgang til et site er lukket,
 * indtil der her gives en tidsbegrænset adgang til én montør - og den lukker af sig selv igen.
 */
export function VpnPage() {
  return (
    <div className="stack" style={{ gap: 16 }}>
      <SessionsCard />
      <SitesCard />
      <TechniciansCard />
      <ConnectionCard />
    </div>
  )
}

type Message = { ok: boolean; text: string } | null

function MessageLine({ message }: { message: Message }) {
  if (!message) return null
  return (
    <div
      className={message.ok ? 'small' : 'error-box'}
      style={{ marginTop: 12, color: message.ok ? 'var(--success)' : undefined }}
    >
      {message.text}
    </div>
  )
}

function errorText(err: unknown, fallback: string) {
  return err instanceof Error ? err.message : fallback
}

const siteStatusLabel: Record<VpnSiteStatus, string> = {
  online: 'Online',
  offline: 'Offline',
  pending_key: 'Mangler routernøgle',
  disabled: 'Spærret',
  missing: 'Findes ikke på gatewayen',
  unknown: 'Ukendt'
}

const siteStatusColor: Record<VpnSiteStatus, string> = {
  online: 'var(--success)',
  offline: '#94A3B8',
  pending_key: '#F59E0B',
  disabled: '#EF4444',
  missing: '#EF4444',
  unknown: '#94A3B8'
}

function SiteStatusPill({ status }: { status: VpnSiteStatus }) {
  return (
    <span className="pill">
      <span className="pill__dot" style={{ background: siteStatusColor[status] }} />
      {siteStatusLabel[status]}
    </span>
  )
}

const endReasonLabel: Record<string, string> = {
  cancelled: 'Annulleret, før den blev åbnet',
  revoked_by_admin: 'Lukket',
  site_disabled: 'Sitet blev spærret',
  technician_revoked: 'Montørens profil blev spærret',
  technician_key_changed: 'Montøren lavede en ny nøgle',
  gateway_restart: 'Gatewayen genstartede',
  firewall_state_lost: 'Gatewayens firewall blev genindlæst'
}

const durations = [
  { minutes: 15, label: '15 minutter' },
  { minutes: 30, label: '30 minutter' },
  { minutes: 60, label: '1 time' },
  { minutes: 120, label: '2 timer' },
  { minutes: 240, label: '4 timer' },
  { minutes: 480, label: '8 timer' }
]

// ---------------- Serviceadgang ----------------

/**
 * Hvor langt LAN-opsætningen er for ét site (beslutning 095).
 *
 * Forskellen på "gemt" og "bekræftet" er hele pointen: felterne i Overblik siger kun, hvad routeren BØR gøre.
 * Først når gatewayen har fået svar fra sitets alias, ved vi, at kommandoerne rent faktisk er indsat.
 */
function LanStatus({ site }: { site: VpnSite }) {
  if (!site.customerLan) return <span className="tiny muted">Ikke sat op</span>
  if (site.lanCheckOk === true) {
    return (
      <span className="tiny" style={{ color: '#15803D' }}>
        ✓ Bekræftet {site.lanCheckedAt ? formatDateTime(site.lanCheckedAt) : ''}
      </span>
    )
  }
  if (site.lanCheckOk === false) {
    return (
      <span className="tiny" style={{ color: '#B91C1C' }}>
        Routeren svarede ikke {site.lanCheckedAt ? formatDateTime(site.lanCheckedAt) : ''}
      </span>
    )
  }
  return <span className="tiny" style={{ color: '#B45309' }}>Ikke bekræftet - er kommandoerne indsat?</span>
}

function SessionsCard() {
  const queryClient = useQueryClient()
  const [showHistory, setShowHistory] = useState(false)
  const sessions = useQuery({
    queryKey: ['vpn-sessions', showHistory],
    queryFn: () => api.get<VpnSessionList>(`/api/vpn/sessions${showHistory ? '' : '?activeOnly=true'}`),
    refetchInterval: 30_000
  })
  const sites = useQuery({ queryKey: ['vpn-sites'], queryFn: () => api.get<VpnSiteList>('/api/vpn/sites') })
  const technicians = useQuery({ queryKey: ['vpn-technicians'], queryFn: () => api.get<VpnTechnician[]>('/api/vpn/technicians') })

  const [siteId, setSiteId] = useState('')
  const [userId, setUserId] = useState('')
  const [duration, setDuration] = useState(120)
  const [reason, setReason] = useState('')
  const [projectId, setProjectId] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<Message>(null)

  const grantableSites = (sites.data?.sites ?? []).filter((s) => s.status === 'online' || s.status === 'offline')
  const grantableTechnicians = (technicians.data ?? []).filter((t) => !t.revoked && t.userIsActive)
  const selectedSite = grantableSites.find((s) => String(s.id) === siteId)

  // Servicesagen er valgfri og skal høre til sitets kunde.
  const customer = useQuery({
    queryKey: ['customer', selectedSite?.customerId],
    queryFn: () => api.get<CustomerDetail>(`/api/customers/${selectedSite!.customerId}`),
    enabled: !!selectedSite
  })

  async function grant() {
    setBusy(true)
    setMessage(null)
    try {
      await api.post<VpnSession>('/api/vpn/sessions', {
        vpnSiteId: Number(siteId),
        userId: Number(userId),
        durationMinutes: duration,
        reason,
        projectId: projectId ? Number(projectId) : null
      })
      setReason('')
      setProjectId('')
      await queryClient.invalidateQueries({ queryKey: ['vpn-sessions'] })
      setMessage({
        ok: true,
        text: 'Adgangen er godkendt. Montøren åbner den selv under "VPN-adgang" inden 24 timer - tiden løber først fra da.'
      })
    } catch (err) {
      setMessage({ ok: false, text: errorText(err, 'Kunne ikke give adgang.') })
    } finally {
      setBusy(false)
    }
  }

  async function end(session: VpnSession) {
    const question =
      session.status === 'pending'
        ? `Annullér godkendelsen af ${session.technicianName} til "${session.siteName}"?`
        : `Luk ${session.technicianName}s adgang til "${session.siteName}" nu? Åbne forbindelser stoppes også.`
    if (!window.confirm(question)) return
    setMessage(null)
    try {
      await api.delete(`/api/vpn/sessions/${session.id}`)
      await queryClient.invalidateQueries({ queryKey: ['vpn-sessions'] })
      setMessage({ ok: true, text: 'Adgangen er lukket.' })
    } catch (err) {
      setMessage({ ok: false, text: errorText(err, 'Kunne ikke lukke adgangen.') })
    }
  }

  return (
    <Card
      title="Serviceadgang"
      actions={
        <label className="row small" style={{ gap: 6 }}>
          <input type="checkbox" style={{ width: 'auto' }} checked={showHistory} onChange={(e) => setShowHistory(e.target.checked)} />
          Vis historik
        </label>
      }
    >
      <p className="small muted" style={{ marginTop: 0 }}>
        Al adgang til kundernes anlæg er lukket. Godkend én montør til ét site i en begrænset tid. Montøren åbner
        selv adgangen inden 24 timer - med sin YubiKey, når det krav er slået til - og den lukker af sig selv igen.
      </p>

      <div className="form-grid">
        <label className="stack" style={{ gap: 4 }}>
          <span className="small">Site</span>
          <select value={siteId} onChange={(e) => { setSiteId(e.target.value); setProjectId('') }}>
            <option value="">Vælg site ...</option>
            {grantableSites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.customerName} - {s.name} ({siteStatusLabel[s.status]})
              </option>
            ))}
          </select>
        </label>

        <label className="stack" style={{ gap: 4 }}>
          <span className="small">Montør</span>
          <select value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">Vælg montør ...</option>
            {grantableTechnicians.map((t) => (
              <option key={t.userId} value={t.userId}>
                {t.displayName}
              </option>
            ))}
          </select>
          <span className="tiny muted">Kun montører, der selv har oprettet deres VPN-profil under "VPN-adgang".</span>
        </label>

        <label className="stack" style={{ gap: 4 }}>
          <span className="small">Varighed</span>
          <select value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
            {durations.map((d) => (
              <option key={d.minutes} value={d.minutes}>
                {d.label}
              </option>
            ))}
          </select>
        </label>

        <label className="stack" style={{ gap: 4 }}>
          <span className="small">Servicesag (valgfri)</span>
          <select value={projectId} onChange={(e) => setProjectId(e.target.value)} disabled={!selectedSite}>
            <option value="">Ingen sag</option>
            {(customer.data?.projects ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="stack" style={{ gap: 4, marginTop: 4 }}>
        <span className="small">Begrundelse</span>
        <textarea rows={2} maxLength={500} placeholder="Fejlsøgning på inverter efter alarm" value={reason} onChange={(e) => setReason(e.target.value)} />
      </label>

      <button
        className="btn btn--primary"
        style={{ marginTop: 12 }}
        disabled={busy || !siteId || !userId || !reason.trim()}
        onClick={() => void grant()}
      >
        {busy ? 'Godkender ...' : 'Godkend adgang'}
      </button>

      <MessageLine message={message} />

      <div style={{ marginTop: 16 }}>
        {sessions.isLoading && <Loading />}
        {sessions.error && <ErrorBox error={sessions.error} />}
        {sessions.data?.gatewayError && (
          <div className="error-box" style={{ marginBottom: 8 }}>
            Gatewayen svarer ikke, så status er ukendt: {sessions.data.gatewayError}
          </div>
        )}
        {sessions.data && sessions.data.sessions.length === 0 && (
          <EmptyState>{showHistory ? 'Der er ikke givet VPN-adgang endnu.' : 'Ingen har adgang lige nu.'}</EmptyState>
        )}
        {sessions.data && sessions.data.sessions.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Montør</th>
                  <th>Site</th>
                  <th>Tid</th>
                  <th>Begrundelse</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sessions.data.sessions.map((s) => (
                  <tr key={s.id}>
                    <td>
                      {s.technicianName}
                      {s.technicianAddress && <div className="tiny muted">{s.technicianAddress}</div>}
                    </td>
                    <td>
                      {s.siteName}
                      <div className="tiny muted">
                        {s.customerName}
                        {s.siteAddress && ` · ${s.siteAddress}`}
                      </div>
                    </td>
                    <td className="small">
                      {s.startsAt ? (
                        <>
                          {formatDateTime(s.startsAt)} - {formatTime(s.expiresAt)}
                        </>
                      ) : (
                        <>
                          Godkendt {formatDateTime(s.grantedAt)}
                          <div className="tiny muted">{s.durationMinutes} min, når den åbnes</div>
                        </>
                      )}
                    </td>
                    <td className="small">
                      {s.reason}
                      {s.projectName && <div className="tiny muted">Sag: {s.projectName}</div>}
                      {s.grantedByName && <div className="tiny muted">Givet af {s.grantedByName}</div>}
                    </td>
                    <td className="small">
                      {s.status === 'active' && <strong style={{ color: 'var(--success)' }}>Aktiv til {formatTime(s.expiresAt)}</strong>}
                      {s.status === 'pending' && (
                        <span style={{ color: '#B45309' }}>Venter på montøren (til {formatDateTime(s.activateBefore)})</span>
                      )}
                      {s.status === 'not_activated' && 'Ikke åbnet i tide'}
                      {s.status === 'cancelled' && 'Annulleret'}
                      {s.status === 'expired' && 'Udløbet'}
                      {s.status === 'revoked' && (endReasonLabel[s.endReason ?? ''] ?? 'Lukket')}
                      {s.status === 'unknown' && 'Ukendt'}
                    </td>
                    <td>
                      {(s.status === 'active' || s.status === 'unknown') && (
                        <button className="btn btn--danger" onClick={() => void end(s)}>
                          Luk nu
                        </button>
                      )}
                      {s.status === 'pending' && (
                        <button className="btn" onClick={() => void end(s)}>
                          Annullér
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  )
}

// ---------------- Sites ----------------

function SitesCard() {
  const queryClient = useQueryClient()
  const sites = useQuery({ queryKey: ['vpn-sites'], queryFn: () => api.get<VpnSiteList>('/api/vpn/sites'), refetchInterval: 60_000 })
  const [creating, setCreating] = useState(false)
  const [template, setTemplate] = useState<{ site: VpnSite; text: string } | null>(null)
  const [lanSiteId, setLanSiteId] = useState<number | null>(null)
  // Routerfilen med den private nøgle. Findes KUN her i hukommelsen, indtil den er hentet én gang.
  const [routerFile, setRouterFile] = useState<RouterFile | null>(null)
  const [busySiteId, setBusySiteId] = useState<number | null>(null)
  const [checkingId, setCheckingId] = useState<number | null>(null)
  const [message, setMessage] = useState<Message>(null)

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['vpn-sites'] })
  }

  // Det, der skal gøres for at LAN-adgangen virker overalt (Simon 2026-09-17). Spærrede sites tæller ikke med.
  const live = sites.data?.sites.filter((s) => s.status !== 'disabled') ?? []
  const needsSetup = live.filter((s) => !s.customerLan)
  const notConfirmed = live.filter((s) => s.customerLan && s.lanCheckOk !== true)

  async function run(action: () => Promise<unknown>, ok: string) {
    setMessage(null)
    try {
      await action()
      await refresh()
      setMessage({ ok: true, text: ok })
    } catch (err) {
      setMessage({ ok: false, text: errorText(err, 'Handlingen mislykkedes.') })
    }
  }

  /**
   * Spørger gatewayen, om routeren rent faktisk har fået sine LAN-kommandoer (beslutning 095).
   * Gatewayen pinger sitets eget alias - vi sender ingen adresse, kun sitets id.
   */
  async function checkLan(site: VpnSite) {
    setCheckingId(site.id)
    setMessage(null)
    try {
      const result = await api.post<VpnLanCheck>(`/api/vpn/sites/${site.id}/lan-check`)
      setMessage({ ok: result.reachable, text: `${site.name}: ${result.message}` })
      await refresh()
    } catch (err) {
      setMessage({ ok: false, text: errorText(err, 'Kunne ikke tjekke LAN-opsætningen.') })
    } finally {
      setCheckingId(null)
    }
  }

  /** Ny nøgle i browseren -> kun den offentlige til serveren -> routerfilen klar til at blive hentet én gang. */
  async function newRouterFile(site: VpnSite) {
    if (
      site.routerPublicKey &&
      !window.confirm(
        `Lav en ny routerfil til "${site.name}"?\n\nDen nuværende fil holder op med at virke med det samme. Routeren er offline, indtil den nye fil er lagt ind.`
      )
    ) {
      return
    }
    setMessage(null)
    setBusySiteId(site.id)
    try {
      const keys = await generateWireGuardKeyPair()
      const updated = await api.put<VpnSite>(`/api/vpn/sites/${site.id}/router-key`, { routerPublicKey: keys.publicKey })
      setRouterFile(toRouterFile(updated, keys.privateKey))
      await refresh()
    } catch (err) {
      setMessage({ ok: false, text: errorText(err, 'Kunne ikke lave en ny routerfil.') })
    } finally {
      setBusySiteId(null)
    }
  }

  async function showTemplate(site: VpnSite) {
    setMessage(null)
    try {
      const result = await api.get<{ text: string }>(`/api/vpn/sites/${site.id}/router-config`)
      setTemplate({ site, text: result.text })
    } catch (err) {
      setMessage({ ok: false, text: errorText(err, 'Kunne ikke hente skabelonen.') })
    }
  }

  function setOwnKey(site: VpnSite) {
    const key = window.prompt(
      `Kun hvis routeren selv har lavet sin nøgle: indsæt routerens OFFENTLIGE nøgle for "${site.name}" (44 tegn, ender på =).\nDen private nøgle bliver på routeren.`,
      site.routerPublicKey ?? ''
    )
    if (!key?.trim()) return
    void run(() => api.put(`/api/vpn/sites/${site.id}/router-key`, { routerPublicKey: key.trim() }), 'Routerens egen nøgle er gemt.')
  }

  // Adressen, hvor sitet er sat op, rettes her OG paa gatewayen; routerens enhed faar navn og adresse med
  // (Simon 2026-09-30).
  const [addressFor, setAddressFor] = useState<number | null>(null)
  const [addressForm, setAddressForm] = useState({ address: '', postalCode: '', city: '' })

  function editAddress(site: VpnSite) {
    setAddressFor(site.id)
    setAddressForm({ address: site.address ?? '', postalCode: site.postalCode ?? '', city: site.city ?? '' })
  }

  function saveAddress(site: VpnSite) {
    void run(() => api.put(`/api/vpn/sites/${site.id}/address`, addressForm), 'Adressen er rettet - også på routerens enhed.')
    setAddressFor(null)
  }

  function disable(site: VpnSite) {
    if (!window.confirm(`Spær "${site.name}"? Routeren kobles af, og al montøradgang til sitet lukkes.`)) return
    void run(() => api.post(`/api/vpn/sites/${site.id}/disable`), 'Sitet er spærret.')
  }

  function remove(site: VpnSite) {
    if (!window.confirm(`Slet "${site.name}"? VPN-adressen og LAN-aliasset kan bruges til et nyt site med det samme. Historikken over adgang bliver.`)) return
    void run(() => api.delete(`/api/vpn/sites/${site.id}`), 'Sitet er slettet.')
  }

  return (
    <Card
      title="Sites"
      actions={
        !creating && (
          <button className="btn btn--primary" onClick={() => setCreating(true)}>
            Opret site
          </button>
        )
      }
    >
      <p className="small muted" style={{ marginTop: 0 }}>
        Et site er et anlæg hos en kunde med én WireGuard-router. Routerens nøgle laves her i browseren, og filen til
        routeren kan hentes <strong>én gang</strong>. Den private nøgle gemmes ingen steder - mangler filen, laves en ny.
      </p>

      {routerFile && (
        <RouterFilePanel
          file={routerFile}
          onDone={(downloaded) => {
            setRouterFile(null)
            setMessage(
              downloaded
                ? { ok: true, text: `Routerfilen til "${routerFile.siteName}" er hentet. Den kan ikke hentes igen - mangler den, så tryk "Ny routerfil".` }
                : { ok: false, text: `Routerfilen til "${routerFile.siteName}" blev ikke hentet. Tryk "Ny routerfil" for at lave en ny.` }
            )
          }}
        />
      )}

      {creating && (
        <CreateSiteForm
          onCancel={() => setCreating(false)}
          onCreated={async (site, file) => {
            setCreating(false)
            await refresh()
            if (file) {
              setRouterFile(file)
            } else {
              setMessage({ ok: true, text: `"${site.name}" er oprettet med VPN-adressen ${site.vpnAddress}. Indsæt routerens egen nøgle bagefter.` })
            }
          }}
        />
      )}

      <MessageLine message={message} />

      {sites.isLoading && <Loading />}
      {sites.error && <ErrorBox error={sites.error} />}
      {sites.data?.gatewayError && (
        <div className="error-box" style={{ marginTop: 8 }}>
          Gatewayen svarer ikke, så status er ukendt: {sites.data.gatewayError}
        </div>
      )}
      {sites.data && sites.data.unknownOnGateway > 0 && (
        <p className="tiny muted">
          Gatewayen har {sites.data.unknownOnGateway} site(s), som Overblik ikke kender. De er oprettet uden om Overblik.
        </p>
      )}
      {needsSetup.length > 0 && (
        <div
          className="small"
          style={{ marginTop: 8, padding: 10, borderRadius: 8, background: '#FEF3C7', border: '1px solid #FCD34D' }}
        >
          <strong>{needsSetup.length === 1 ? '1 site mangler LAN-opsætning' : `${needsSetup.length} sites mangler LAN-opsætning`}:</strong>{' '}
          {needsSetup.map((s) => s.name).join(', ')}. Montøren kan kun nå routeren selv, ikke kundens udstyr.
        </div>
      )}
      {notConfirmed.length > 0 && (
        <div
          className="small"
          style={{ marginTop: 8, padding: 10, borderRadius: 8, background: '#F1F5F9', border: '1px solid #CBD5E1' }}
        >
          <strong>Ikke bekræftet:</strong> {notConfirmed.map((s) => s.name).join(', ')}. LAN'et er udfyldt her, men
          gatewayen har ikke fået svar fra routeren. Tryk "Tjek opsætningen" på sitet.
        </div>
      )}

      {sites.data && sites.data.sites.length === 0 && !creating && <EmptyState>Der er ingen sites endnu.</EmptyState>}

      {sites.data && sites.data.sites.length > 0 && (
        <div className="table-wrap" style={{ marginTop: 12 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Site</th>
                <th>Kunde</th>
                <th>VPN-adresse</th>
                <th>Kundens LAN</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sites.data.sites.map((s) => (
                <tr key={s.id}>
                  <td>
                    {s.name}{' '}
                    {addressFor !== s.id && (
                      <button className="link-button tiny" style={linkStyle} onClick={() => editAddress(s)}>
                        Ret adresse
                      </button>
                    )}
                    {addressFor === s.id && (
                      <div className="stack" style={{ gap: 4, margin: '6px 0' }}>
                        <input
                          placeholder="Adresse, fx Grønkærvej 24"
                          maxLength={150}
                          value={addressForm.address}
                          onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })}
                        />
                        <div className="row" style={{ gap: 4 }}>
                          <input
                            style={{ width: 80 }}
                            placeholder="Postnr."
                            maxLength={10}
                            value={addressForm.postalCode}
                            onChange={(e) => setAddressForm({ ...addressForm, postalCode: e.target.value })}
                          />
                          <input
                            placeholder="By"
                            maxLength={40}
                            value={addressForm.city}
                            onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                          />
                        </div>
                        <div className="btn-group">
                          <button
                            className="btn btn--sm btn--primary"
                            disabled={!addressForm.address.trim() || !addressForm.postalCode.trim() || !addressForm.city.trim()}
                            onClick={() => saveAddress(s)}
                          >
                            Gem adresse
                          </button>
                          <button className="btn btn--sm" onClick={() => setAddressFor(null)}>
                            Annullér
                          </button>
                        </div>
                        <span className="tiny muted">Rettes også på VPN-gatewayen og på routerens enhed.</span>
                      </div>
                    )}
                    {s.deviceName && <div className="tiny muted">Enhed: {s.deviceName}</div>}
                    {s.routerDeviceId ? (
                      <div className="tiny muted">
                        Router: <Link to={`/devices/${s.routerDeviceId}`}>{s.routerDeviceName}</Link>
                      </div>
                    ) : (
                      <div className="tiny">
                        <button
                          className="link-button"
                          style={linkStyle}
                          onClick={() =>
                            void run(
                              () => api.post(`/api/vpn/sites/${s.id}/router-device`, { routerModelId: null }),
                              `Routeren på "${s.name}" er oprettet som enhed.`
                            )
                          }
                        >
                          Opret routeren som enhed
                        </button>
                      </div>
                    )}
                    <div className="tiny">
                      <Link to={`/admin/vpn/sites/${s.id}/label`}>Print label</Link>
                    </div>
                  </td>
                  <td>{s.customerName}</td>
                  <td>
                    <code>{s.vpnAddress ?? '-'}</code>
                  </td>
                  <td>
                    {s.customerLan && (
                      <>
                        <code>{s.customerLan}</code>
                        {s.lanPrefix && (
                          <div className="tiny muted">
                            som <code>{s.lanPrefix}</code> · routeren <code>{s.routerLanAddress}</code>
                          </div>
                        )}
                      </>
                    )}
                    <div>
                      <LanStatus site={s} />
                    </div>
                  </td>
                  <td>
                    <SiteStatusPill status={s.status} />
                    {s.lastHandshakeAt && <div className="tiny muted">Sidst set {formatDateTime(s.lastHandshakeAt)}</div>}
                    {s.inUseBy && s.inUseUntil && (
                      <div className="tiny" style={{ color: '#B45309' }}>
                        I brug af {s.inUseBy} til kl. {formatTime(s.inUseUntil)}
                      </div>
                    )}
                  </td>
                  <td>
                    {s.status === 'disabled' ? (
                      <div className="btn-group">
                        <button className="btn" onClick={() => void run(() => api.post(`/api/vpn/sites/${s.id}/enable`), 'Sitet er genåbnet.')}>
                          Genåbn
                        </button>
                        <button className="btn btn--danger" onClick={() => remove(s)}>
                          Slet
                        </button>
                      </div>
                    ) : (
                      <div className="stack" style={{ gap: 6 }}>
                        <div className="btn-group">
                          <button
                            className="btn btn--primary"
                            disabled={busySiteId !== null || routerFile !== null}
                            onClick={() => void newRouterFile(s)}
                          >
                            {busySiteId === s.id ? 'Laver fil ...' : 'Ny routerfil'}
                          </button>
                          <button className="btn btn--danger" onClick={() => disable(s)}>
                            Spær
                          </button>
                        </div>
                        <div className="tiny">
                          <button className="link-button" style={linkStyle} onClick={() => setOwnKey(s)}>
                            Routerens egen nøgle
                          </button>
                          {' · '}
                          <button className="link-button" style={linkStyle} onClick={() => void showTemplate(s)}>
                            Skabelon uden nøgle
                          </button>
                          {' · '}
                          <button className="link-button" style={linkStyle} onClick={() => setLanSiteId(s.id)}>
                            LAN-adgang og EdgeRouter
                          </button>
                          {s.customerLan && (
                            <>
                              {' · '}
                              <button
                                className="link-button"
                                style={linkStyle}
                                disabled={checkingId !== null}
                                onClick={() => void checkLan(s)}
                              >
                                {checkingId === s.id ? 'Tjekker ...' : 'Tjek opsætningen'}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lanSiteId !== null && sites.data?.sites.find((s) => s.id === lanSiteId) && (
        <LanPanel
          key={lanSiteId}
          site={sites.data.sites.find((s) => s.id === lanSiteId)!}
          onClose={() => setLanSiteId(null)}
          onSaved={refresh}
        />
      )}

      {template && (
        <div style={{ marginTop: 16 }}>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <strong>Skabelon uden nøgle: {template.site.name}</strong>
            <button className="btn" onClick={() => setTemplate(null)}>
              Luk
            </button>
          </div>
          <p className="tiny muted">
            Kun til routere, der selv laver deres nøgle. Indtast værdierne i routeren, og indsæt bagefter routerens
            offentlige nøgle med "Routerens egen nøgle".
          </p>
          <pre className="small" style={{ whiteSpace: 'pre-wrap', background: '#F1F5F9', padding: 12, borderRadius: 8 }}>
            {template.text}
          </pre>
        </div>
      )}
    </Card>
  )
}

const linkStyle = { background: 'none', border: 0, padding: 0, color: 'var(--primary, #2563EB)', cursor: 'pointer', textDecoration: 'underline' }

/**
 * LAN-adgang (beslutning 094): kundens LAN, montørens adresse på det og routerens LAN-interface - og de
 * EdgeRouter-kommandoer, der skal ind i routeren. Gemmes med sikkerhedsnøgle, fordi det giver adgang til kundens net.
 */
function LanPanel({ site, onClose, onSaved }: { site: VpnSite; onClose: () => void; onSaved: () => Promise<void> }) {
  const [customerLan, setCustomerLan] = useState(site.customerLan ?? '')
  const [routerLanAddress, setRouterLanAddress] = useState(site.routerLanAddress ?? '')
  const [routerLanInterface, setRouterLanInterface] = useState(site.routerLanInterface ?? 'eth1')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<Message>(null)
  const [commands, setCommands] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [checking, setChecking] = useState(false)
  const [check, setCheck] = useState<VpnLanCheck | null>(null)

  async function save(clear: boolean) {
    if (clear && !window.confirm(`Fjern LAN-adgangen på "${site.name}"? Husk også at fjerne NAT-reglerne i routeren.`)) return
    setBusy(true)
    setMessage(null)
    setCommands(null)
    try {
      const updated = await api.put<VpnSite>(
        `/api/vpn/sites/${site.id}/lan`,
        clear ? { customerLan: null } : { customerLan, routerLanAddress, routerLanInterface }
      )
      setRouterLanAddress(updated.routerLanAddress ?? '')
      await onSaved()
      setMessage({
        ok: true,
        text: clear ? 'LAN-adgangen er fjernet.' : 'LAN-adgangen er gemt. Routeren skal opdateres med kommandoerne herunder.'
      })
      if (!clear) await showCommands()
    } catch (err) {
      setMessage({ ok: false, text: errorText(err, 'Kunne ikke gemme LAN-adgangen.') })
    } finally {
      setBusy(false)
    }
  }

  async function showCommands() {
    try {
      const result = await api.get<{ text: string }>(`/api/vpn/sites/${site.id}/router-config/edgeos`)
      setCommands(result.text)
      setCopied(false)
    } catch (err) {
      setMessage({ ok: false, text: errorText(err, 'Kunne ikke hente EdgeRouter-opsætningen.') })
    }
  }

  async function copy() {
    if (!commands) return
    await navigator.clipboard.writeText(commands)
    setCopied(true)
  }

  /** Gatewayen pinger sitets alias. Svarer det, er kommandoerne rent faktisk inde på routeren. */
  async function runCheck() {
    setChecking(true)
    setMessage(null)
    setCheck(null)
    try {
      setCheck(await api.post<VpnLanCheck>(`/api/vpn/sites/${site.id}/lan-check`))
      await onSaved()
    } catch (err) {
      setMessage({ ok: false, text: errorText(err, 'Kunne ikke tjekke LAN-opsætningen.') })
    } finally {
      setChecking(false)
    }
  }

  const example = site.customerLan && site.lanPrefix ? aliasExample(site.customerLan, site.lanPrefix) : null

  return (
    <div className="stack" style={{ gap: 10, marginTop: 16, padding: 12, border: '1px solid #CBD5E1', borderRadius: 8 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <strong>LAN-adgang: {site.name}</strong>
        <button className="btn" onClick={onClose}>
          Luk
        </button>
      </div>
      <p className="small" style={{ margin: 0 }}>
        Montøren arbejder på kundens LAN gennem sitets alias <code>{site.lanPrefix ?? '(gatewayen svarer ikke)'}</code>. Routeren
        oversætter til kundens LAN, og kundens udstyr ser montøren som routerens egen adresse. Kun én montør ad gangen.
        {example && (
          <>
            {' '}
            Eksempel: <code>{example}</code>.
          </>
        )}
      </p>
      <div className="form-grid">
        <label className="stack" style={{ gap: 4 }}>
          <span className="small">Kundens LAN (/24)</span>
          <input value={customerLan} placeholder="10.122.1.0/24" onChange={(e) => setCustomerLan(e.target.value)} />
        </label>
        <label className="stack" style={{ gap: 4 }}>
          <span className="small">Routerens LAN-adresse</span>
          <input value={routerLanAddress} placeholder="standard .240" onChange={(e) => setRouterLanAddress(e.target.value)} />
        </label>
        <label className="stack" style={{ gap: 4 }}>
          <span className="small">Routerens LAN-interface</span>
          <input value={routerLanInterface} placeholder="eth1" onChange={(e) => setRouterLanInterface(e.target.value)} />
        </label>
      </div>
      <p className="tiny muted" style={{ margin: 0 }}>
        Routerens egen adresse på kundens LAN - <strong>den primære adresse på interfacet</strong>, ikke en ekstra. Tom = .240.
        Montøren optræder som netop den adresse over for kundens udstyr. Interfacet er den port på routeren, der sidder på
        kundens LAN (fx eth1, br0, switch0 eller eth1.10).
      </p>
      <div className="btn-group">
        <button className="btn btn--primary" disabled={busy || !customerLan.trim()} onClick={() => void save(false)}>
          {busy ? 'Gemmer ...' : 'Gem LAN-adgang'}
        </button>
        <button className="btn" disabled={busy} onClick={() => void showCommands()}>
          Vis EdgeRouter-opsætning
        </button>
        {site.customerLan && (
          <button className="btn" disabled={busy || checking} onClick={() => void runCheck()}>
            {checking ? 'Tjekker ...' : 'Tjek opsætningen'}
          </button>
        )}
        {site.customerLan && (
          <button className="btn btn--danger" disabled={busy} onClick={() => void save(true)}>
            Fjern LAN-adgang
          </button>
        )}
      </div>
      <MessageLine message={message} />
      {check && (
        <div
          className="small"
          style={{
            padding: 10,
            borderRadius: 8,
            background: check.reachable ? '#DCFCE7' : '#FEE2E2',
            border: `1px solid ${check.reachable ? '#86EFAC' : '#FCA5A5'}`
          }}
        >
          <strong>{check.reachable ? '✓ Opsætningen er på plads' : 'Ikke bekræftet'}</strong>
          <div>{check.message}</div>
          <div className="tiny muted">
            Gatewayen pingede <code>{check.address}</code>
            {check.rttMs != null && <> · {Math.round(check.rttMs)} ms</>} · {formatDateTime(check.checkedAt)}
          </div>
        </div>
      )}
      {commands && (
        <div>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <strong className="small">EdgeRouter (EdgeOS) - log ind med SSH og indsæt</strong>
            <button className="btn" onClick={() => void copy()}>
              {copied ? 'Kopieret' : 'Kopiér'}
            </button>
          </div>
          <p className="tiny muted">
            Uden privat nøgle. NAT-reglerne har numrene 4100 og 5100 - tjek at routeren ikke bruger dem i forvejen.
          </p>
          <p className="tiny" style={{ color: '#B45309', marginTop: 0 }}>
            <strong>Brug SSH, ikke routerens web-CLI.</strong> CLI-vinduet i EdgeOS' web-UI taber linjeskiftene, når man
            indsætter flere linjer - så limes kommandoerne sammen til én, og intet bliver udført. Log ind med fx PuTTY på
            routerens VPN-adresse {site.vpnAddress && <code>{site.vpnAddress}</code>}, indsæt, og tryk derefter "Tjek opsætningen".
          </p>
          <pre className="small" style={{ whiteSpace: 'pre-wrap', background: '#F1F5F9', padding: 12, borderRadius: 8 }}>
            {commands}
          </pre>
        </div>
      )}
    </div>
  )
}

interface RouterFile {
  siteName: string
  fileName: string
  /** Hele konfigurationen MED den private nøgle. Kun i hukommelsen. */
  config: string
  /** Samme nøgle som EdgeRouter CLI-kommandoer. Kun én af de to filer kan hentes. */
  edgeOsFileName: string
  edgeOsCommands: string
  /** Valgt i opret-formularen: vis kun den ene fil. Uden valg (Ny routerfil) vælges i panelet. */
  only?: RouterFileKind
}

type RouterFileKind = 'wireguard' | 'edgeos'

/**
 * @param edgeOsLan LAN-kommandoerne fra serveren (uden nøgle). Lægges efter tunnelen i CLI-filen, så den indeholder
 * alt, routeren skal have.
 */
function toRouterFile(site: VpnSite, privateKey: string, only?: RouterFileKind, edgeOsLan?: string): RouterFile {
  if (!site.vpnAddress || !site.endpoint) {
    throw new Error('Gatewayen svarede uden adresse. Nøglen er skiftet - tryk "Ny routerfil" igen om lidt.')
  }
  const siteInfo = { name: site.name, vpnAddress: site.vpnAddress }
  return {
    siteName: site.name,
    fileName: routerConfigFileName(site.id),
    config: buildRouterConfig(privateKey, siteInfo, site.endpoint),
    edgeOsFileName: edgeOsCommandsFileName(site.id),
    edgeOsCommands: buildEdgeOsRouterCommands(privateKey, siteInfo, site.endpoint) + (edgeOsLan ? `\n${edgeOsLan}` : ''),
    only
  }
}

/**
 * Filen kan hentes ÉN gang. Når der er trykket "Hent", glemmes den private nøgle, og panelet lukker.
 * Forlades siden inden, advarer browseren - ellers skal der laves en ny nøgle.
 */
function RouterFilePanel({ file, onDone }: { file: RouterFile; onDone: (downloaded: boolean) => void }) {
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [])

  // Kun ÉN af de to filer kan hentes - bagefter glemmes nøglen, uanset hvilken der blev valgt.
  function download(kind: RouterFileKind) {
    if (kind === 'wireguard') downloadTextFile(file.fileName, file.config)
    else downloadTextFile(file.edgeOsFileName, file.edgeOsCommands)
    onDone(true)
  }

  function discard() {
    if (!window.confirm('Kassér filen uden at hente den? Den kan ikke laves igen - der skal laves en ny nøgle.')) return
    onDone(false)
  }

  return (
    <div
      className="stack"
      style={{ gap: 10, marginBottom: 16, padding: 12, border: '2px solid #F59E0B', borderRadius: 8, background: '#FFFBEB' }}
    >
      <strong>Routerfil til "{file.siteName}" er klar</strong>
      <p className="small" style={{ margin: 0 }}>
        Filen indeholder routerens <strong>private nøgle</strong> og kan kun hentes <strong>én gang</strong>
        {!file.only && ' - vælg den type, routeren skal bruge'}. Slet den fra computeren, når den er lagt ind. Mistes den,
        laves en ny med "Ny routerfil".
      </p>
      <div className="btn-group">
        {file.only !== 'edgeos' && (
          <button className="btn btn--primary" onClick={() => download('wireguard')}>
            Hent WireGuard-fil ({file.fileName})
          </button>
        )}
        {file.only !== 'wireguard' && (
          <button className="btn btn--primary" onClick={() => download('edgeos')}>
            Hent EdgeRouter CLI ({file.edgeOsFileName})
          </button>
        )}
        <button className="btn" onClick={discard}>
          Kassér
        </button>
      </div>
      <p className="tiny" style={{ margin: 0 }}>
        <strong>Næste:</strong> indsæt indholdet på routeren <strong>over SSH</strong> - ikke i CLI-vinduet i routerens
        web-UI, som taber linjeskiftene, så kommandoerne limes sammen til én linje og intet bliver udført. Tryk derefter
        "Tjek opsætningen" på sitet, så gatewayen bekræfter, at montøren kan nå kundens LAN.
      </p>
    </div>
  )
}

function CreateSiteForm({
  onCancel,
  onCreated
}: {
  onCancel: () => void
  onCreated: (site: VpnSite, file: RouterFile | null) => void | Promise<void>
}) {
  // Ét id pr. formular: trykkes der igen efter en timeout, opretter gatewayen ikke et site mere.
  const requestId = useMemo(() => crypto.randomUUID(), [])
  // Samme nøgle ved et nyt forsøg - ellers ville gatewayen se samme requestId med andet indhold.
  const keys = useRef<WireGuardKeyPair | null>(null)
  const customers = useQuery({ queryKey: ['customers'], queryFn: () => api.get<Customer[]>('/api/customers') })
  const [customerId, setCustomerId] = useState('')
  const devices = useQuery({
    queryKey: ['devices', { customerId }],
    queryFn: () => api.get<Device[]>(`/api/devices?customerId=${customerId}`),
    enabled: !!customerId
  })
  const [deviceId, setDeviceId] = useState('')
  // Routeren bliver en enhed hos kunden (beslutning 105) - modellen er valgfri.
  const deviceTypes = useDeviceTypes()
  const routerModels = deviceTypes.data?.find((t) => t.name.toLowerCase() === 'router')?.models ?? []
  const [routerModelId, setRouterModelId] = useState('')
  // En ER-6P faar grundopsaetningen i CLI-filen, og LAN'et er broen br0 (beslutning 107). En EdgeRouter X
  // ligesaa, med LAN'et i switch0 (beslutning 122). Samme regel som VpnLan.IsEr6p/IsErX.
  const chosenModel = routerModels.find((m) => String(m.id) === routerModelId)?.name ?? ''
  const er6p = /er-6p/i.test(chosenModel)
  const erx = /(edgerouter x|er-x)/i.test(chosenModel) && !/sfp/i.test(chosenModel)
  // Adressen, hvor sitet sættes op - bliver sitets navn og routerens adresse (Simon 2026-09-30).
  const [address, setAddress] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [city, setCity] = useState('')
  // Hvordan routeren sættes op: tekst til EdgeRouter-CLI, WireGuard-fil, eller routeren laver selv nøglen.
  const [setup, setSetup] = useState<RouterFileKind | 'own'>('edgeos')
  const routerMakesKey = setup === 'own'
  const [customerLan, setCustomerLan] = useState('')
  const [routerLanAddress, setRouterLanAddress] = useState('')
  const [routerLanInterface, setRouterLanInterface] = useState('eth1')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function save() {
    setBusy(true)
    setError(null)
    try {
      if (!routerMakesKey && !keys.current) {
        keys.current = await generateWireGuardKeyPair()
      }
      const site = await api.post<VpnSite>('/api/vpn/sites', {
        requestId,
        customerId: Number(customerId),
        deviceId: deviceId ? Number(deviceId) : null,
        routerModelId: routerModelId ? Number(routerModelId) : null,
        address: address.trim(),
        postalCode: postalCode.trim(),
        city: city.trim(),
        routerPublicKey: routerMakesKey ? null : keys.current!.publicKey,
        customerLan: customerLan.trim(),
        routerLanAddress: routerLanAddress.trim() || null,
        routerLanInterface: er6p ? 'br0' : erx ? 'switch0' : routerLanInterface.trim() || null
      })
      if (routerMakesKey) {
        await onCreated(site, null)
        return
      }
      // CLI-filen skal indeholde alt: tunnel + nøgle herfra, LAN-delen fra serveren (uden nøgle).
      let lan: string | undefined
      if (setup === 'edgeos' && site.customerLan) {
        try {
          lan = (await api.get<{ text: string }>(`/api/vpn/sites/${site.id}/router-config/edgeos`)).text
        } catch {
          lan = '# LAN-kommandoerne kunne ikke hentes. Hent dem under "LAN-adgang og EdgeRouter" på sitet.\n'
        }
      }
      await onCreated(site, toRouterFile(site, keys.current!.privateKey, setup as RouterFileKind, lan))
    } catch (err) {
      setError(errorText(err, 'Kunne ikke oprette sitet.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="stack" style={{ gap: 12, marginBottom: 16, padding: 12, border: '1px solid var(--border, #E2E8F0)', borderRadius: 8 }}>
      <div>
        <strong>Nyt site</strong>
        <p className="tiny muted" style={{ margin: '2px 0 0' }}>
          Tre trin: <strong>1)</strong> udfyld herunder · <strong>2)</strong> hent routerfilen og indsæt den på routeren
          over SSH · <strong>3)</strong> tryk "Tjek opsætningen" på sitet. Så ved du, at montøren kan nå kundens LAN - og
          ikke kun routeren.
        </p>
      </div>

      <div className="form-grid">
        <label className="stack" style={{ gap: 4 }}>
          <span className="small">Kunde</span>
          <select value={customerId} disabled={busy} onChange={(e) => { setCustomerId(e.target.value); setDeviceId('') }}>
            <option value="">Vælg kunde ...</option>
            {(customers.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <label className="stack" style={{ gap: 4 }}>
          <span className="small">Enhed (valgfri)</span>
          <select
            value={deviceId}
            disabled={!customerId || busy}
            onChange={(e) => setDeviceId(e.target.value)}
          >
            <option value="">Ingen enhed</option>
            {(devices.data ?? []).map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
                {d.deviceType ? ` (${d.deviceType})` : ''}
              </option>
            ))}
          </select>
        </label>

        <label className="stack" style={{ gap: 4 }}>
          <span className="small">Adresse, hvor routeren sættes op</span>
          <input maxLength={150} disabled={busy} placeholder="Grønkærvej 24" value={address} onChange={(e) => setAddress(e.target.value)} />
        </label>

        <div className="row" style={{ gap: 8 }}>
          <label className="stack" style={{ gap: 4, width: 90 }}>
            <span className="small">Postnr.</span>
            <input maxLength={10} disabled={busy} placeholder="7660" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
          </label>
          <label className="stack" style={{ gap: 4, flex: 1 }}>
            <span className="small">By</span>
            <input maxLength={40} disabled={busy} placeholder="Bækmarksbro" value={city} onChange={(e) => setCity(e.target.value)} />
          </label>
        </div>

        <label className="stack" style={{ gap: 4 }}>
          <span className="small">Routerens model</span>
          <select value={routerModelId} disabled={busy} onChange={(e) => setRouterModelId(e.target.value)}>
            <option value="">Ikke valgt</option>
            {routerModels.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
                {m.manufacturer ? ` (${m.manufacturer})` : ''}
              </option>
            ))}
          </select>
          <span className="tiny muted">
            {er6p
              ? 'ER-6P: CLI-filen får også grundopsætningen - WAN på eth0 (DHCP), eth1-eth5 i broen br0 med routerens LAN-adresse, DHCP-server og masquerade ud på WAN.'
              : erx
                ? 'EdgeRouter X: CLI-filen får også grundopsætningen - WAN på eth0 (DHCP), eth1-eth4 i switchen switch0 med routerens LAN-adresse, DHCP-server og masquerade ud på WAN.'
                : 'Routeren oprettes som enhed hos kunden, så den får QR-mærkat og historik. Nye modeller tilføjes under Enhedstyper.'}
          </span>
        </label>
      </div>

      <div className="tiny muted" style={{ marginBottom: -4 }}>
        Kundens LAN skal udfyldes nu. Uden det kan routeren ikke oversætte, og montøren når kun routeren selv.
      </div>
      <div className="form-grid">
        <label className="stack" style={{ gap: 4 }}>
          <span className="small">Routerens opsætning</span>
          <select value={setup} disabled={busy || keys.current !== null} onChange={(e) => setSetup(e.target.value as RouterFileKind | 'own')}>
            <option value="edgeos">Tekstfil til EdgeRouter CLI</option>
            <option value="wireguard">WireGuard-konfigurationsfil (.conf)</option>
            <option value="own">Routeren laver selv sin nøgle (ingen fil)</option>
          </select>
          <span className="tiny muted">
            {setup === 'edgeos'
              ? 'Kommandoer til at indsætte efter "configure" - med nøgle, tunnel og LAN-adgang, hvis kundens LAN er udfyldt.'
              : setup === 'wireguard'
                ? 'Importeres i routerens WireGuard-opsætning.'
                : 'Indsæt routerens offentlige nøgle bagefter med "Routerens egen nøgle".'}
          </span>
        </label>

        <label className="stack" style={{ gap: 4 }}>
          <span className="small">Kundens LAN (/24) *</span>
          <input disabled={busy} placeholder="10.122.1.0/24" value={customerLan} onChange={(e) => setCustomerLan(e.target.value)} />
        </label>

        <>
          <label className="stack" style={{ gap: 4 }}>
            <span className="small">Routerens LAN-adresse</span>
            <input disabled={busy} placeholder="standard .240" value={routerLanAddress} onChange={(e) => setRouterLanAddress(e.target.value)} />
          </label>
          <label className="stack" style={{ gap: 4 }}>
            <span className="small">Routerens LAN-interface</span>
            <input disabled={busy || er6p || erx} placeholder="eth1" value={er6p ? 'br0' : erx ? 'switch0' : routerLanInterface} onChange={(e) => setRouterLanInterface(e.target.value)} />
          </label>
        </>
      </div>

      {error && <div className="error-box">{error}</div>}

      <div className="btn-group">
        <button className="btn btn--primary" disabled={busy || !customerId || !address.trim() || !postalCode.trim() || !city.trim() || !customerLan.trim()} onClick={() => void save()}>
          {busy ? 'Opretter ...' : routerMakesKey ? 'Opret site' : 'Opret site og lav routerfil'}
        </button>
        <button className="btn" disabled={busy} onClick={onCancel}>
          Annullér
        </button>
      </div>
    </div>
  )
}

// ---------------- Montører ----------------

function TechniciansCard() {
  const queryClient = useQueryClient()
  const technicians = useQuery({ queryKey: ['vpn-technicians'], queryFn: () => api.get<VpnTechnician[]>('/api/vpn/technicians') })
  const [message, setMessage] = useState<Message>(null)

  async function revoke(t: VpnTechnician) {
    if (!window.confirm(`Spær ${t.displayName}s VPN-profil? Brug det ved en mistet telefon. Al adgang lukkes med det samme, og montøren skal lave en ny nøgle.`)) return
    setMessage(null)
    try {
      await api.post(`/api/vpn/technicians/${t.userId}/revoke`)
      await queryClient.invalidateQueries({ queryKey: ['vpn-technicians'] })
      await queryClient.invalidateQueries({ queryKey: ['vpn-sessions'] })
      setMessage({ ok: true, text: 'Profilen er spærret.' })
    } catch (err) {
      setMessage({ ok: false, text: errorText(err, 'Kunne ikke spærre profilen.') })
    }
  }

  return (
    <Card title="Montørernes VPN-profiler">
      <p className="small muted" style={{ marginTop: 0 }}>
        Montøren opretter selv sin profil under "VPN-adgang". Profilen giver ingen adgang i sig selv.
      </p>
      <MessageLine message={message} />
      {technicians.isLoading && <Loading />}
      {technicians.error && <ErrorBox error={technicians.error} />}
      {technicians.data && technicians.data.length === 0 && <EmptyState>Ingen montører har oprettet en profil endnu.</EmptyState>}
      {technicians.data && technicians.data.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Montør</th>
                <th>VPN-adresse</th>
                <th>Sidst forbundet</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {technicians.data.map((t) => (
                <tr key={t.userId}>
                  <td>
                    {t.displayName}
                    {!t.userIsActive && <div className="tiny muted">Brugeren er deaktiveret</div>}
                  </td>
                  <td>
                    <code>{t.vpnAddress}</code>
                  </td>
                  <td className="small">{formatDateTime(t.lastHandshakeAt)}</td>
                  <td>
                    {t.revoked ? (
                      <span className="small muted">Spærret</span>
                    ) : (
                      <button className="btn btn--danger" onClick={() => void revoke(t)}>
                        Spær profil
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

// ---------------- Forbindelsen ----------------

function ConnectionCard() {
  const queryClient = useQueryClient()
  const status = useQuery({ queryKey: ['vpn-status'], queryFn: () => api.get<VpnStatus>('/api/vpn/status') })
  const [baseUrl, setBaseUrl] = useState<string | null>(null)
  const [contextId, setContextId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<Message>(null)

  async function saveStepUp(enabled: boolean) {
    const id = (contextId ?? status.data?.stepUpAuthContextId ?? '').trim()
    const question = enabled
      ? `Kræv YubiKey på VPN med authentication context "${id}"?\n\nDu skal selv bekræfte med din YubiKey nu. Lykkes det ikke, forbliver kravet slået fra.`
      : 'Slå kravet om YubiKey FRA? Det kræver din YubiKey at slå fra.'
    if (!window.confirm(question)) return
    setBusy(true)
    setMessage(null)
    try {
      await api.put('/api/vpn/settings', { baseUrl: status.data?.baseUrl, stepUpEnabled: enabled, stepUpAuthContextId: id })
      await queryClient.invalidateQueries({ queryKey: ['vpn-status'] })
      setContextId(null)
      setMessage({ ok: true, text: enabled ? 'VPN kræver nu YubiKey.' : 'Kravet om YubiKey er slået fra.' })
    } catch (err) {
      setMessage({ ok: false, text: errorText(err, 'Kunne ikke gemme.') })
    } finally {
      setBusy(false)
    }
  }

  async function save() {
    setBusy(true)
    setMessage(null)
    try {
      await api.put('/api/vpn/settings', { baseUrl: baseUrl ?? status.data?.baseUrl })
      await queryClient.invalidateQueries({ queryKey: ['vpn-status'] })
      setBaseUrl(null)
      setMessage({ ok: true, text: 'Adressen er gemt.' })
    } catch (err) {
      setMessage({ ok: false, text: errorText(err, 'Kunne ikke gemme.') })
    } finally {
      setBusy(false)
    }
  }

  if (status.isLoading) return <Loading />
  if (status.error) return <ErrorBox error={status.error} />
  const data = status.data!
  const health = data.health

  return (
    <Card
      title="Forbindelsen til gatewayen"
      actions={
        <button className="btn" onClick={() => void queryClient.invalidateQueries({ queryKey: ['vpn-status'] })}>
          Tjek igen
        </button>
      }
    >
      {health ? (
        <p className="small" style={{ marginTop: 0, color: 'var(--success)' }}>
          Forbundet. Gateway version {health.version} · {health.wgSites.online} af {health.wgSites.peers} routere online ·{' '}
          {health.wgTechs.peers} montørprofiler · {health.activeSessions} aktive adgange
          {health.lastBackupAt ? ` · seneste backup ${formatDateTime(health.lastBackupAt)}` : ' · ingen backup registreret'}
        </p>
      ) : (
        <div className="error-box">{data.gatewayError ?? 'Gatewayen svarer ikke.'}</div>
      )}

      <ul className="small" style={{ margin: '8px 0', paddingLeft: 18 }}>
        <li>Klientcertifikat: {data.hasClientCertificate ? 'fundet' : 'MANGLER'}</li>
        <li>Klientnøgle: {data.hasClientKey ? 'fundet' : 'MANGLER'}</li>
        <li>Gatewayens CA: {data.hasCaCertificate ? 'fundet' : 'MANGLER'}</li>
      </ul>
      <p className="tiny muted">
        Filerne ligger på serveren i /etc/tcpss/vpn og kan ikke ses eller ændres herfra. Forbindelsen går gennem
        tunnelen wg-mgmt (INSTALL.md afsnit 5i).
      </p>

      <label className="stack" style={{ gap: 4, marginTop: 8 }}>
        <span className="small">Gatewayens adresse</span>
        <input type="url" value={baseUrl ?? data.baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
        <span className="tiny muted">Skal være https. Standarden er https://10.60.0.1:8443/api/v1/ - adressen inde i tunnelen.</span>
      </label>
      <button className="btn" style={{ marginTop: 8 }} disabled={busy || baseUrl === null} onClick={() => void save()}>
        Gem adresse
      </button>

      <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border, #E2E8F0)' }}>
        <strong>🔑 Kræv YubiKey</strong>
        <p className="small" style={{ margin: '6px 0' }}>
          {data.stepUpEnabled ? (
            <span style={{ color: 'var(--success)' }}>
              Slået til (authentication context <code>{data.stepUpAuthContextId}</code>).
            </span>
          ) : (
            <span className="muted">Slået fra.</span>
          )}{' '}
          Når det er slået til, skal der bekræftes med YubiKey for at oprette site, lave routerfil, genåbne et site,
          godkende adgang og ændre denne opsætning - og montøren skal bruge sin YubiKey for at lave sin nøgle og åbne
          adgang. At lukke, spærre og annullere kræver det aldrig.
        </p>
        <p className="tiny muted" style={{ marginTop: 0 }}>
          Kræver en Conditional Access-politik i Microsoft Entra på samme authentication context (INSTALL.md afsnit 5j).
          Lokale brugere uden Microsoft 365 kan ikke bruge VPN, når kravet er slået til.
        </p>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label className="stack" style={{ gap: 4, width: 180 }}>
            <span className="small">Authentication context</span>
            <input
              maxLength={3}
              placeholder="c1"
              value={contextId ?? data.stepUpAuthContextId ?? ''}
              onChange={(e) => setContextId(e.target.value)}
            />
          </label>
          {data.stepUpEnabled ? (
            <button className="btn btn--danger" disabled={busy} onClick={() => void saveStepUp(false)}>
              Slå fra
            </button>
          ) : (
            <button
              className="btn btn--primary"
              disabled={busy || !/^c([1-9]|[1-9][0-9])$/.test((contextId ?? data.stepUpAuthContextId ?? '').trim())}
              onClick={() => void saveStepUp(true)}
            >
              Slå til
            </button>
          )}
        </div>
      </div>
      <MessageLine message={message} />
    </Card>
  )
}
